// The photo as it leaves the device (D-92): the camera's file decoded with
// its orientation applied, scaled so the long side is at most 2048 px, and
// re-encoded as JPEG at the highest quality that lands at 500 KB or less,
// which for a photo of that size is the 300 to 500 KB of the target; a
// smaller picture simply stays smaller. Re-encoding through a canvas writes
// no metadata: the orientation survives in the pixels and every other EXIF
// tag, the camera's position included, is gone. Then its SHA-256, the
// fingerprint the declaration carries and the server verifies.

/** The server's limit (D-101); the device refuses anything above it before sending. */
export const MAX_PHOTO_BYTES = 8 * 1024 * 1024;
export const LONG_SIDE_PX = 2048;
export const TARGET_MAX_BYTES = 500 * 1024;

const QUALITIES = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35] as const;

export type Compressor = (file: Blob) => Promise<Blob>;

interface Surface {
  draw: (bitmap: ImageBitmap) => void;
  encode: (quality: number) => Promise<Blob>;
}

function surface(width: number, height: number): Surface {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (context === null) throw new Error('no 2d context');
    return {
      draw: (bitmap) => {
        context.drawImage(bitmap, 0, 0, width, height);
      },
      encode: (quality) => canvas.convertToBlob({ type: 'image/jpeg', quality }),
    };
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('no 2d context');
  return {
    draw: (bitmap) => {
      context.drawImage(bitmap, 0, 0, width, height);
    },
    encode: (quality) =>
      new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob === null) reject(new Error('encoding failed'));
            else resolve(blob);
          },
          'image/jpeg',
          quality,
        );
      }),
  };
}

/** The camera's file as the device keeps and sends it. Rejects when it cannot be decoded. */
export async function compressPhoto(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, LONG_SIDE_PX / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const target = surface(width, height);
    target.draw(bitmap);
    let encoded: Blob | null = null;
    for (const quality of QUALITIES) {
      encoded = await target.encode(quality);
      if (encoded.size <= TARGET_MAX_BYTES) break;
    }
    if (encoded === null) throw new Error('encoding failed');
    return encoded;
  } finally {
    bitmap.close();
  }
}

/** SHA-256 of the bytes, lowercase hexadecimal, as the API compares it. */
export async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
